import AdornmentCommon = require("Presentation/Scripts/TFS/TFS.Adornment.Common");
import AdornmentCommonUI = require("Presentation/Scripts/TFS/TFS.Adornment.Common.UI");
import TFS_Extension = require("BuiltInExtensions/Scripts/TFS.Extension");
import VSS = require("VSS/VSS");

export interface IMonacoAdornmentControl {
    // Show the adornment control
    show(): void;
    // Dispose the adornment control
    dispose(): void;
}

// A "decoration" adornment, which allows text in the editor to be highlighted/styled
export class MonacoAdornmentDecorationControl implements IMonacoAdornmentControl {
    public _adornment: AdornmentCommon.DecorationAdornment;
    public _editor: any;
    private _decorationId: number;

    constructor(adornment: AdornmentCommon.DecorationAdornment, adornmentControlManager: MonacoAdornmentControlManager) {
        this._adornment = adornment;
        this._editor = adornmentControlManager.getEditor(adornment.isOriginalSide);
    }

    public show() {
        if (!this._decorationId) {
            this._decorationId = this._editor.changeDecorations((changeAccessor) => {
                return changeAccessor.addDecoration({
                    startLineNumber: this._adornment.startLine,
                    startColumn: this._adornment.startColumn,
                    endLineNumber: this._adornment.endLine,
                    endColumn: this._adornment.endColumn
                }, {
                        isWholeLine: Boolean(this._adornment.isWholeLine),
                        inlineClassName: this._adornment.className,
                        className: this._adornment.actualClassName,
                        glyphMarginClassName: this._adornment.glyphMarginClassName,
                        hoverMessage: this._adornment.glyphMarginText,
                        linesDecorationsClassName: this._adornment.linesDecorationsClassName,
                        overviewRuler: this._adornment.overviewRuler
                    });
            });

            if (this._adornment.scrollToAdornment) {
                this.scrollIntoAdornmentViewByRange();
            }
        }
    }

    public dispose() {
        if (this._decorationId) {
            this._editor.changeDecorations((changeAccessor) => {
                changeAccessor.removeDecoration(this._decorationId);
            });
            this._decorationId = null;
        }
    }

    public scrollIntoAdornmentView() {
        this._editor.revealPosition({
            lineNumber: this._adornment.startLine,
            column: this._adornment.endColumn
        }, true, true);
    }

    public scrollIntoAdornmentViewByRange() {
        // Don't scroll the adornment to code editor's center.
        // Rather perform minimum scroll(both horizontal and vertical) so that adornment is easily visible.
        this._editor.revealRangeInCenterIfOutsideViewport({
            startLineNumber: this._adornment.startLine,
            startColumn: this._adornment.startColumn,
            endLineNumber: this._adornment.startLine,
            endColumn: this._adornment.endColumn
        });
    }
}

// A "decoration" adornment for GUTTER adornments, which allows line numbers in the editor to be highlighted
class MonacoGutterAdornmentControl implements IMonacoAdornmentControl {
    constructor(private adornment: AdornmentCommon.GutterAdornment, private gutterAdornmentWidget: GutterAdornmentsWidget) {
    }

    public show() {
        this.gutterAdornmentWidget.addAdornment(this.adornment);
    }

    public dispose() {
        this.gutterAdornmentWidget.removeAdornment(this.adornment);
    }
}

export class MonacoAdornmentControlManager implements AdornmentCommonUI.IAdornmentControlManager {

    private _editor: any;
    private _isDiffEditor: boolean;
    private _adornmentControls: IMonacoAdornmentControl[];
    private _modifiedEditorGutterWidget: GutterAdornmentsWidget;
    private _originalEditorGutterWidget: GutterAdornmentsWidget;

    constructor(editor: any, isDiffEditor: boolean) {
        this._editor = editor;
        this._isDiffEditor = isDiffEditor;
        this._adornmentControls = [];
        this._initializeGutterAdornmentWidgets();
    }

    public dispose() {
        this.removeAdornments();
    }

    public getEditor(originalSide: boolean) {
        if (this._isDiffEditor) {
            return originalSide ? this._editor.originalEditor : this._editor.modifiedEditor;
        }
        else {
            return this._editor;
        }
    }

    // Set and draw adornments onto the editor
    public setAdornments(adornments: AdornmentCommon.Adornment[]) {
        this.removeAdornments();
        this.addAdornments(adornments);
    }

    // Add adornments to the editor
    public addAdornments(adornments: AdornmentCommon.Adornment[]) {
        if (adornments && adornments.length) {
            $.each(adornments, (i: number, adornment: AdornmentCommon.Adornment) => {
                if (adornment.isOriginalSide || this._isDiffEditor) {
                    const adornmentControl = this._createAdornmentControl(adornment);
                    this._adornmentControls.push(adornmentControl);
                    adornmentControl.show();
                }
            });
        }
    }

    // Remove any adornments from the editor
    public removeAdornments() {
        $.each(this._adornmentControls, (i: number, adornmentControl: IMonacoAdornmentControl) => {
            adornmentControl.dispose();
        });
        this._adornmentControls = [];
    }

    /** Called when the status of an adornment control has changed. */
    public onSetAdornmentStatus(adornmentId: number, statusId: string) {
        TFS_Extension.sendMessage(TFS_Extension.Adornment.ActionIds.SET_STATUS, {
            adornmentId: adornmentId,
            statusId: statusId
        });
    }

    /** Creates a single adornment control */
    private _createAdornmentControl(adornment: AdornmentCommon.Adornment): IMonacoAdornmentControl {
        if (adornment.adornmentType === AdornmentCommon.AdornmentType.COMMENTSTYLE) {
            // Create a comment adornment control
            // COMMENTSTYLE removed because Code Analysis features were discontinued in 2014/2015.  Additional archived file history can be found at
            // https://mseng.visualstudio.com/VSOnline_Archive/_git/Tfs?path=%2FWebAccess%2FBuiltInExtensions%2FScripts%2FTFS.Extensions.CodeEditor.Adornments.ts&version=GBmaster&_a=history
            throw new Error("Implementation of AdornmentCommon.AdornmentType.COMMENTSTYLE has been removed because it is not known to be used.");
        } else if (adornment.adornmentType === AdornmentCommon.AdornmentType.DECORATION) {
            // Create a decoration adornment control
            return new MonacoAdornmentDecorationControl(<AdornmentCommon.DecorationAdornment>adornment, this);
        } else if (adornment.adornmentType === AdornmentCommon.AdornmentType.GUTTER) {
            // Create a gutter adornment control
            const widget = adornment.isOriginalSide ? this._originalEditorGutterWidget : this._modifiedEditorGutterWidget;
            return new MonacoGutterAdornmentControl(<AdornmentCommon.GutterAdornment>adornment, widget);
        }
    }

    /** Creates a container element in gutter of the editor for gutter adornments */
    private _initializeGutterAdornmentWidgets() {
        if (this._isDiffEditor) {
            this._originalEditorGutterWidget = this._addGutterAdornmentWidgetToEditor(true);
            this._modifiedEditorGutterWidget = this._addGutterAdornmentWidgetToEditor(false);
        }
        else {
            this._originalEditorGutterWidget = this._addGutterAdornmentWidgetToEditor(true);
        }
    }

    private _addGutterAdornmentWidgetToEditor(original: boolean): GutterAdornmentsWidget {
        const editor = this.getEditor(original);
        const gutterAdornmentWidget = new GutterAdornmentsWidget(editor, original);
        editor.addOverlayWidget(gutterAdornmentWidget);
        return gutterAdornmentWidget;
    }
}

/**
 * Work item for this work: https://dev.azure.com/mseng/AzureDevOps/_workitems/edit/1362189/
 * This widget enables adding gutter type adornments to monaco editor
 * In particular, it takes care of rendering GutterAdornments
 */
class GutterAdornmentsWidget implements monaco.editor.IOverlayWidget {

    private _$domNode: JQuery = null;
    private _editorFontInfo: monaco.editor.BareFontInfo;
    private _lineNumberWidth: number;
    private _adornmentCollection: { [id: string]: AdornmentCommon.GutterAdornment };
    private _adornmentElementCollection: { [id: string]: JQuery };

    constructor(private _editor: monaco.editor.ICodeEditor, private _isOriginal: boolean) {

        this._editorFontInfo = this._editor.getOption(monaco.editor.EditorOption.fontInfo);
        this._lineNumberWidth = this._editor.getLayoutInfo().lineNumbersWidth;

        this._adornmentCollection = {};
        this._adornmentElementCollection = {};

        this._subscribeToEditorEvents();
    }

    public getId() {
        return "gutter-adornment-zone-widget-" + this._isOriginal ? "original" : "modified";
    }

    public getDomNode() {
        if (!this._$domNode) {
            this._$domNode = $("<div />").addClass("gutter-adornments-container");
        }
        return this._$domNode[0];
    }

    public getPosition() {
        return null;
    }

    public removeAdornment(adornment: AdornmentCommon.GutterAdornment) {
        if (this._adornmentCollection[adornment.id] === adornment) {
            this._adornmentElementCollection[adornment.id].remove();
            delete this._adornmentCollection[adornment.id];
            delete this._adornmentElementCollection[adornment.id];
        }
    }

    public addAdornment(adornment: AdornmentCommon.GutterAdornment) {

        const startLine = adornment.startLine > 0 ? adornment.startLine : 1;
        const endLine = adornment.endLine;

        if (startLine < endLine) {

            let adornmentHeight = this._editor.getTopForLineNumber(endLine) - this._editor.getTopForLineNumber(startLine);

            if (endLine > this._editor.getModel().getLineCount()) {
                adornmentHeight += this._editorFontInfo.lineHeight;
            }

            // Create dom node for adornment
            let adornmentNode = $("<div />").addClass(adornment.className).css({
                "position": "absolute",
                "top": this._editor.getTopForLineNumber(adornment.startLine) + "px",
                "line-height": this._editorFontInfo.lineHeight + "px",
                "font-family": this._editorFontInfo.fontFamily,
                "font-size": this._editorFontInfo.fontSize,
                "font-weight": this._editorFontInfo.fontWeight,
                "letter-spacing": this._editorFontInfo.letterSpacing,
                "width": this._lineNumberWidth + "px",
                "text-align": "right",
                "height": adornmentHeight.toString() + "px"
            });

            // Create line number container
            const lineNumberContainer = $("<div />").addClass("line-number-container");
            if (adornment.showLineNumber) {
                this._appendAdornmentLineNumbers(startLine, endLine, lineNumberContainer);
                adornmentNode = adornmentNode.append(lineNumberContainer);
            }

            // If helptext is not empty then create tool tip host
            if (adornment.helpText) {
                VSS.using(["VersionControl/Scripts/Components/PullRequestReview/AdornmentToolTipContainerHost"], (reactToolTip: any) => {
                    const toolTipContainer: JQuery = reactToolTip.AdornmentToolTipContainerHost.createToolTipContainer(adornment.helpText);
                    adornmentNode = adornmentNode.append(toolTipContainer);
                },
                          () => { console.warn("'VersionControl/Scripts/Components/PullRequestReview/AdornmentToolTipContainerHost' did not load"); }
                );
            }

            // Append to main container
            this._$domNode = this._$domNode.append(adornmentNode);

            // cache adornment
            this._adornmentCollection[adornment.id] = adornment;

            // remote element if there was a conflict in cache
            if (this._adornmentElementCollection[adornment.id]) {
                this._adornmentElementCollection[adornment.id].remove();
            }

            // cache adornment node
            this._adornmentElementCollection[adornment.id] = adornmentNode;
        }
    }

    private _subscribeToEditorEvents() {
        // onDidChangeViewZones is not a part of the Monaco.Editor.d.ts declaration but is part of the monaco API
        (this._editor as any).onDidChangeViewZones(() => {
            this._updateAdornments();
        });

        this._editor.onDidScrollChange((e: monaco.IScrollEvent) => {
            const scrollTop = e.scrollTop || 0;
            const top = this._editor.getTopForLineNumber(0) - scrollTop;
            this.getDomNode().style.top = top.toString() + "px";
        });

        this._editor.onDidLayoutChange((e: monaco.editor.EditorLayoutInfo) => {
            if (e.lineNumbersWidth !== this._lineNumberWidth) {
                this._lineNumberWidth = e.lineNumbersWidth;
                this._updateAdornmentsLayout();
            }
        });
    }

    /**
     * Gutter width can change when content loads
     * For example before the file has been loaded the default width is 33px
     * If content has more than 99 lines, the gutter width adjusts to account for the 3rd char
     */
    private _updateAdornmentsLayout() {
        for (const key in this._adornmentElementCollection) {
            if (this._adornmentElementCollection.hasOwnProperty(key)) {
                const adornmentNode = this._adornmentElementCollection[key];

                adornmentNode.css({
                    "width": this._lineNumberWidth + "px",
                });
            }
        }
    }

    /** Needs to be called whenever there is a change in viewzone */
    private _updateAdornments() {
        for (const key in this._adornmentElementCollection) {
            if (this._adornmentElementCollection.hasOwnProperty(key)) {
                const adornment = this._adornmentCollection[key];
                const adornmentNode = this._adornmentElementCollection[key];

                const startLine = adornment.startLine > 0 ? adornment.startLine : 1;
                const endLine = adornment.endLine;

                const startLineTop = this._editor.getTopForLineNumber(startLine);

                adornmentNode.css({
                    "top": startLineTop + "px",
                    "height": (this._editor.getTopForLineNumber(endLine - 1) - startLineTop + this._editorFontInfo.lineHeight).toString() + "px"
                });

                const lineNumberContainer = adornmentNode.find(".line-number-container");
                lineNumberContainer.empty();

                if (adornment.showLineNumber === true) {
                    this._appendAdornmentLineNumbers(startLine, endLine, lineNumberContainer);
                }
            }
        }
    }

    private _appendAdornmentLineNumbers(startLine: number, endLine: number, container: JQuery) {
        for (let i = startLine, prevTop; i < endLine; i++) {
            const top = this._editor.getTopForLineNumber(i);

            if (prevTop && prevTop + this._editorFontInfo.lineHeight !== top) {
                // append empty space between lines to adjust for view zones
                container = container.append($("<div />").addClass("view-zone-block").css({
                    "height": (top - prevTop - this._editorFontInfo.lineHeight).toString() + "px"
                }));
            }

            const lineElement = $("<div />").css({
                height: this._editorFontInfo.lineHeight + "px"
            }).html(i.toString());
            container = container.append(lineElement);

            prevTop = top;
        }
    }
}
